<script setup>
import { computed, onBeforeUnmount, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/store/auth'
import { safeReturnTo, homeFor } from '@/utils/navigation.mjs'
import { messageOf } from '@/utils/messages'
import AppIcon from '@/components/portal/AppIcon.vue'

const auth = useAuthStore(), route = useRoute(), router = useRouter()
useHead({ title: '로그인 · TECHEDU' })
const mberId = ref(''), password = ref(''), busy = ref(false), error = ref('')
const manager = ref(typeof route.query.returnTo === 'string' && route.query.returnTo.startsWith('/manager/'))
const supported = location.protocol === 'https:' && Boolean(navigator.locks)
const notice = computed(() => ({ password: '비밀번호를 변경했습니다. 새 비밀번호로 로그인해 주세요.', logout: '모든 기기에서 로그아웃했습니다.', idle: '1시간 동안 활동이 없어 자동으로 로그아웃했습니다. 다시 로그인해 주세요.' })[route.query.notice])
let active = true
onBeforeUnmount(() => { active = false; password.value = '' })
async function login() {
  if (busy.value) return
  busy.value = true; error.value = ''
  const secret = password.value; password.value = ''
  try {
    const identity = await auth.client.login(mberId.value.trim(), secret)
    if (!active) return
    await router.replace(route.query.returnTo ? safeReturnTo(route.query.returnTo) : manager.value ? '/manager/account' : homeFor(identity.roles))
  } catch (cause) {
    // 비밀번호 확인 뒤 영역이 거부된 계정은 올바른 로그인 영역을 안내한다.
    if (active) error.value = cause.status === 403 && cause.code === 'FORBIDDEN'
      ? '해당 계정으로 사용자 영역에 접속할 수 없습니다. 관리자·운영자는 관리자 로그인 페이지를 이용해 주세요.'
      : messageOf(cause)
  }
  finally { if (active) busy.value = false }
}
</script>
<template>
  <section class="portal-section">
    <div class="portal-container portal-auth-layout">
      <aside class="portal-auth-aside"><span class="portal-auth-symbol"><AppIcon name="graduation" :size="28" /></span><h2>학습을 이어서<br>시작하세요</h2><p>나의 계정을 안전하게 관리하고,<br>새로운 배움을 준비하세요.</p><ul><li><AppIcon name="shield" :size="18" />등록된 계정으로 안전하게 로그인하세요.</li><li><AppIcon name="lock" :size="18" />비밀번호는 다른 사람과 공유하지 마세요.</li><li><AppIcon name="monitor" :size="18" />공용 기기에서는 사용 후 로그아웃해 주세요.</li></ul><NuxtLink to="/support"><AppIcon name="headset" :size="18" />학습지원센터</NuxtLink></aside>
      <section class="login-card portal-auth-card" aria-labelledby="login-title">
        <div class="role-tabs" role="group" aria-label="접속 영역"><button type="button" :aria-pressed="!manager" :class="{ active: !manager }" :disabled="busy" @click="manager = false">학습자</button><button type="button" :aria-pressed="manager" :class="{ active: manager }" :disabled="busy" @click="manager = true">기업 교육담당자</button></div>
        <p class="section-kicker">{{ manager ? '기업 교육담당자 계정' : '나의 학습 계정' }}</p><h1 id="login-title">로그인</h1><p class="muted">{{ manager ? '교육담당자 권한이 있는 계정으로 접속하세요.' : '등록된 아이디와 비밀번호를 입력해 주세요.' }}</p>
        <v-alert v-if="notice" variant="tonal" type="success" class="mb-5">{{ notice }}</v-alert>
        <v-alert v-if="!supported" variant="tonal" type="warning" class="mb-5">HTTPS 주소와 Web Locks를 지원하는 최신 브라우저로 접속해 주세요.</v-alert>
        <v-alert v-if="error" role="alert" variant="tonal" type="error" class="mb-5">{{ error }}</v-alert>
        <form @submit.prevent="login">
          <v-text-field v-model="mberId" label="아이디" autocomplete="username" maxlength="200" :disabled="busy" required />
          <v-text-field v-model="password" label="비밀번호" type="password" autocomplete="current-password" maxlength="200" :disabled="busy" required />
          <v-btn type="submit" block size="large" :loading="busy" :disabled="!supported || !mberId.trim() || !password">로그인</v-btn>
          <p class="mt-5"><NuxtLink to="/password-reset">비밀번호를 잊으셨나요?</NuxtLink></p>
        </form>
        <p class="login-tip"><v-icon size="17" icon="mdi-shield-check-outline" /> 공용 기기에서는 사용 후 로그아웃해 주세요.</p>
        <a class="admin-link" href="/admin/login">관리자·운영자 로그인 <span aria-hidden="true">↗</span></a>
        <p class="portal-auth-help"><NuxtLink to="/signup">회원가입 안내</NuxtLink><span aria-hidden="true"> · </span><NuxtLink to="/support">계정 문의</NuxtLink></p>
    </section>
    </div>
  </section>
</template>
